import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Loader2,
  FileText,
  Truck,
  Wrench,
  Check,
  PenTool,
  Trash2,
  User,
  Home,
  Package,
  Plus,
  Pencil,
  Mountain,
  Camera
} from 'lucide-react';

const MobileCreateDocket = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const jobDate = searchParams.get('date');
  const { api, operator } = useMobileAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Docket items
  const [items, setItems] = useState([]);
  const [workCompleted, setWorkCompleted] = useState(''); // Single work completed field
  const [notes, setNotes] = useState('');
  
  // Customer sign-off
  const [customerName, setCustomerName] = useState('');
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const [signature, setSignature] = useState(null);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Material loads
  const [materialLoads, setMaterialLoads] = useState([]);
  const [yards, setYards] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [editingLoadIndex, setEditingLoadIndex] = useState(null);
  const [loadForm, setLoadForm] = useState({
    yard_id: '',
    material_type_id: '',
    load_type: 'pickup',
    quantity: '',
    unit: 'tonne',
    tip_docket_number: '',
    tip_docket_photo: null,
    notes: ''
  });
  const photoInputRef = useRef(null);

  useEffect(() => {
    fetchJobDetail();
    fetchMaterialYards();
  }, [jobId]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#ffd108';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [job]);

  const fetchMaterialYards = async () => {
    try {
      const response = await api.get('/api/mobile/material-yards');
      setYards(response.data.yards || []);
      setMaterialTypes(response.data.material_types || []);
    } catch (error) {
      console.error('Failed to fetch material yards', error);
    }
  };

  const fetchJobDetail = async () => {
    try {
      const response = await api.get(`/api/mobile/jobs/${jobId}`);
      setJob(response.data);
      
      // Initialize items from job
      const initialItems = [];
      
      // Get equipment info from allocation
      const equipment = response.data.equipment;
      const equipmentRego = equipment?.registration || equipment?.name || 'N/A';
      
      // Add equipment type item with default times (8am-5pm = 9 hours)
      initialItems.push({
        id: 'equipment-' + Date.now(),
        item_type: 'equipment',
        type_name: response.data.equipment_type,
        asset_id: equipmentRego, // Equipment registration/ID
        qty: 9,
        uom: 'HOUR',
        start_time: '08:00',
        finish_time: '17:00',
        lunch_minutes: 0,
        travel_minutes: 0
      });
      
      // Add attachment items with default times
      if (response.data.attachment_types) {
        response.data.attachment_types.forEach((att, idx) => {
          initialItems.push({
            id: 'attachment-' + idx + '-' + Date.now(),
            item_type: 'attachment',
            type_name: att.name,
            qty: 9,
            uom: 'HOUR',
            start_time: '08:00',
            finish_time: '17:00',
            lunch_minutes: 0,
            travel_minutes: 0
          });
        });
      }
      
      // Add worker type items from worker_allocations (actual workers assigned)
      // or fall back to worker_requirements if no allocations
      const workerAllocations = response.data.worker_allocations || [];
      const workerTypes = response.data.worker_types || [];
      
      if (workerAllocations.length > 0) {
        // Use actual worker allocations - includes worker names
        workerAllocations.forEach((wa, idx) => {
          const workerType = workerTypes.find(wt => wt.id === wa.worker_type_id);
          initialItems.push({
            id: 'worker-' + wa.id + '-' + Date.now(),
            item_type: 'worker',
            type_name: workerType?.name || 'Worker',
            worker_name: wa.worker_name || 'Assigned Worker', // Actual worker name
            worker_type_id: wa.worker_type_id,
            qty: 9,
            uom: 'HOUR',
            start_time: '08:00',
            finish_time: '17:00',
            lunch_minutes: 0,
            travel_minutes: 0
          });
        });
      } else if (response.data.worker_requirements) {
        // Fall back to requirements if no allocations
        response.data.worker_requirements.forEach((wr, idx) => {
          const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
          if (workerType) {
            for (let i = 0; i < wr.quantity; i++) {
              initialItems.push({
                id: 'worker-' + wr.worker_type_id + '-' + i + '-' + Date.now(),
                item_type: 'worker',
                type_name: workerType.name,
                worker_name: 'Not Assigned',
                worker_type_id: wr.worker_type_id,
                qty: 9,
                uom: 'HOUR',
                start_time: '08:00',
                finish_time: '17:00',
                lunch_minutes: 0,
                travel_minutes: 0
              });
            }
          }
        });
      }
      
      setItems(initialItems);
    } catch (error) {
      toast.error('Failed to fetch job details');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        
        // Calculate hours if time fields are set and uom is HOUR
        // Formula: (Finish - Start) - Lunch + Travel
        if ((field === 'start_time' || field === 'finish_time' || field === 'lunch_minutes' || field === 'travel_minutes') && updatedItem.uom === 'HOUR') {
          if (updatedItem.start_time && updatedItem.finish_time) {
            const hours = calculateHours(
              updatedItem.start_time, 
              updatedItem.finish_time,
              updatedItem.lunch_minutes || 0,
              updatedItem.travel_minutes || 0
            );
            updatedItem.qty = hours;
          }
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateHours = (startTime, finishTime, lunchMinutes = 0, travelMinutes = 0) => {
    try {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [finishHour, finishMin] = finishTime.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMin;
      let finishMinutes = finishHour * 60 + finishMin;
      
      // Handle overnight shifts
      if (finishMinutes < startMinutes) {
        finishMinutes += 24 * 60;
      }
      
      // Calculate: (Finish - Start) - Lunch + Travel
      const totalMinutes = finishMinutes - startMinutes - lunchMinutes + travelMinutes;
      return Math.round((totalMinutes / 60) * 100) / 100; // Round to 2 decimals
    } catch (error) {
      return 0;
    }
  };

  // Generate hours (00-23) and minutes (00, 05, 10, ..., 55) for dropdowns
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
  
  // Generate lunch/travel options in 15-minute increments (0, 15, 30, ..., 180)
  const lunchTravelOptions = Array.from({ length: 13 }, (_, i) => i * 15);

  const handleTimeChange = (itemId, timeType, timePart, value) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const currentTime = item[timeType] || '00:00';
    const [currentHour, currentMin] = currentTime.split(':');
    
    const newTime = timePart === 'hour' 
      ? `${value}:${currentMin}`
      : `${currentHour}:${value}`;
    
    handleItemChange(itemId, timeType, newTime);
  };

  // Signature drawing handlers
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Material Load Functions
  const resetLoadForm = () => {
    setLoadForm({
      yard_id: '',
      material_type_id: '',
      load_type: 'pickup',
      quantity: '',
      unit: 'tonne',
      tip_docket_number: '',
      tip_docket_photo: null,
      notes: ''
    });
    setEditingLoadIndex(null);
  };

  const openLoadDialog = (index = null) => {
    if (index !== null) {
      const load = materialLoads[index];
      setLoadForm({
        yard_id: load.yard_id,
        material_type_id: load.material_type_id,
        load_type: load.load_type,
        quantity: load.quantity.toString(),
        unit: load.unit,
        tip_docket_number: load.tip_docket_number || '',
        tip_docket_photo: load.tip_docket_photo || null,
        notes: load.notes || ''
      });
      setEditingLoadIndex(index);
    } else {
      resetLoadForm();
    }
    setLoadDialogOpen(true);
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLoadForm(prev => ({ ...prev, tip_docket_photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveLoad = () => {
    // Validation
    if (!loadForm.yard_id) {
      toast.error('Please select a location');
      return;
    }
    if (!loadForm.material_type_id) {
      toast.error('Please select a material type');
      return;
    }
    if (!loadForm.quantity || parseFloat(loadForm.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const selectedYard = yards.find(y => y.id === loadForm.yard_id);
    const selectedMaterial = materialTypes.find(m => m.id === loadForm.material_type_id);

    const newLoad = {
      yard_id: loadForm.yard_id,
      yard_name: selectedYard?.name || 'Unknown',
      material_type_id: loadForm.material_type_id,
      material_name: selectedMaterial?.name || 'Unknown',
      load_type: loadForm.load_type,
      quantity: parseFloat(loadForm.quantity),
      unit: loadForm.unit,
      tip_docket_number: loadForm.tip_docket_number || null,
      tip_docket_photo: loadForm.tip_docket_photo || null,
      notes: loadForm.notes || null
    };

    if (editingLoadIndex !== null) {
      const updated = [...materialLoads];
      updated[editingLoadIndex] = newLoad;
      setMaterialLoads(updated);
      toast.success('Load updated');
    } else {
      setMaterialLoads(prev => [...prev, newLoad]);
      toast.success('Load added');
    }

    setLoadDialogOpen(false);
    resetLoadForm();
  };

  const handleDeleteLoad = (index) => {
    setMaterialLoads(prev => prev.filter((_, i) => i !== index));
    toast.success('Load removed');
  };

  const getAvailableMaterials = () => {
    // If a yard is selected, filter materials to those available at that yard
    if (loadForm.yard_id) {
      const yard = yards.find(y => y.id === loadForm.yard_id);
      if (yard?.materials) {
        const yardMaterialIds = yard.materials.map(m => m.material_type_id);
        return materialTypes.filter(mt => yardMaterialIds.includes(mt.id));
      }
    }
    return materialTypes;
  };

  const getAvailableUnits = () => {
    // If yard and material are selected, get available units for that combo
    if (loadForm.yard_id && loadForm.material_type_id) {
      const yard = yards.find(y => y.id === loadForm.yard_id);
      if (yard?.materials) {
        const matchingMaterials = yard.materials.filter(m => m.material_type_id === loadForm.material_type_id);
        return matchingMaterials.map(m => m.unit);
      }
    }
    return ['tonne', 'm3', 'load'];
  };

  const getLoadTypeLabel = (type) => {
    const labels = { pickup: 'Pickup', disposal: 'Disposal', buyback: 'Buy Back' };
    return labels[type] || type;
  };

  const getLoadTypeColor = (type) => {
    const colors = {
      pickup: 'bg-blue-500/20 text-blue-400',
      disposal: 'bg-orange-500/20 text-orange-400',
      buyback: 'bg-green-500/20 text-green-400'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  const handleSubmit = async () => {
    // Validation
    const filledItems = items.filter(item => item.qty > 0);
    if (filledItems.length === 0) {
      toast.error('Please enter hours for at least one item');
      return;
    }
    
    if (!customerName.trim()) {
      toast.error('Please enter customer name for sign-off');
      return;
    }
    
    if (!customerConfirmed) {
      toast.error('Customer must confirm the docket');
      return;
    }

    setSaving(true);
    
    try {
      // Get signature as base64 if drawn
      let signatureData = null;
      if (hasSignature && canvasRef.current) {
        signatureData = canvasRef.current.toDataURL('image/png');
      }
      
      // Get user's LOCAL date (not UTC) for timezone handling
      const now = new Date();
      const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      const docketData = {
        job_id: jobId,
        job_date: jobDate,
        user_today: userToday, // Pass user's local "today" for timezone handling
        items: filledItems.map(item => ({
          item_type: item.item_type,
          type_name: item.type_name,
          qty: parseFloat(item.qty) || 0,
          uom: item.uom,
          description: '', // Individual descriptions removed - using workCompleted instead
          start_time: item.start_time || null,
          finish_time: item.finish_time || null,
          worker_type_id: item.worker_type_id || null,
          lunch_minutes: item.lunch_minutes || 0,
          travel_minutes: item.travel_minutes || 0
        })),
        material_loads: materialLoads.map(load => ({
          yard_id: load.yard_id,
          material_type_id: load.material_type_id,
          load_type: load.load_type,
          quantity: load.quantity,
          unit: load.unit,
          tip_docket_number: load.tip_docket_number,
          tip_docket_photo: load.tip_docket_photo,
          notes: load.notes
        })),
        notes: notes,
        work_completed: workCompleted, // Single work completed field
        customer_name: customerName,
        customer_signature: signatureData,
        customer_confirmed: customerConfirmed
      };
      
      await api.post('/api/mobile/dockets', docketData);
      toast.success('Docket submitted successfully!');
      navigate('/mobile/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit docket');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] pb-24">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-400 p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-white font-bold flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#ffd108]" />
                Create Docket
              </h1>
              <p className="text-gray-400 text-xs">{job?.customer?.company_name}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')}
            className="text-gray-400 p-2"
            data-testid="home-btn"
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Hours Entry */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">Hours Worked</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="border border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  {item.item_type === 'equipment' ? (
                    <Truck className="h-4 w-4 text-[#ffd108]" />
                  ) : item.item_type === 'worker' ? (
                    <User className="h-4 w-4 text-blue-400" />
                  ) : (
                    <Wrench className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-white font-medium">{item.type_name}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {item.item_type}
                  </Badge>
                </div>
                
                {/* Show Worker Name for workers - equipment type is already shown in header */}
                {item.item_type === 'worker' && item.worker_name && (
                  <div className="text-xs text-blue-400 mb-3 ml-6">
                    {item.worker_name}
                  </div>
                )}
                
                {/* Custom time selects with 5-minute increments */}
                <div className="space-y-3">
                  {/* Start Time */}
                  <div>
                    <Label className="text-gray-400 text-xs">Start Time</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Select
                        value={item.start_time ? item.start_time.split(':')[0] : '08'}
                        onValueChange={(v) => handleTimeChange(item.id, 'start_time', 'hour', v)}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white">
                          <SelectValue placeholder="HH" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {hours.map(h => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={item.start_time ? item.start_time.split(':')[1] : '00'}
                        onValueChange={(v) => handleTimeChange(item.id, 'start_time', 'minute', v)}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {minutes.map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Finish Time */}
                  <div>
                    <Label className="text-gray-400 text-xs">Finish Time</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Select
                        value={item.finish_time ? item.finish_time.split(':')[0] : '17'}
                        onValueChange={(v) => handleTimeChange(item.id, 'finish_time', 'hour', v)}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white">
                          <SelectValue placeholder="HH" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {hours.map(h => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={item.finish_time ? item.finish_time.split(':')[1] : '00'}
                        onValueChange={(v) => handleTimeChange(item.id, 'finish_time', 'minute', v)}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {minutes.map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Lunch and Travel - Side by Side */}
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-700">
                    {/* Lunch Break */}
                    <div>
                      <Label className="text-gray-400 text-xs flex items-center gap-1">
                        <span className="text-red-400">−</span> Lunch Break
                      </Label>
                      <Select
                        value={String(item.lunch_minutes || 0)}
                        onValueChange={(v) => handleItemChange(item.id, 'lunch_minutes', parseInt(v))}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {lunchTravelOptions.map(mins => (
                            <SelectItem key={mins} value={String(mins)}>
                              {mins === 0 ? 'None' : `${mins} min`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Travel Time */}
                    <div>
                      <Label className="text-gray-400 text-xs flex items-center gap-1">
                        <span className="text-green-400">+</span> Travel Time
                      </Label>
                      <Select
                        value={String(item.travel_minutes || 0)}
                        onValueChange={(v) => handleItemChange(item.id, 'travel_minutes', parseInt(v))}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {lunchTravelOptions.map(mins => (
                            <SelectItem key={mins} value={String(mins)}>
                              {mins === 0 ? 'None' : `${mins} min`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {item.start_time && item.finish_time && (
                  <div className="bg-[#ffd108]/10 border border-[#ffd108]/30 rounded p-2 mt-3">
                    <p className="text-[#ffd108] text-sm font-medium">
                      Calculated: {item.qty || 0} hours
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      ({item.start_time} → {item.finish_time}) 
                      {item.lunch_minutes > 0 && ` − ${item.lunch_minutes}min lunch`}
                      {item.travel_minutes > 0 && ` + ${item.travel_minutes}min travel`}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Material Loads */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Package className="h-4 w-4 text-[#ffd108]" />
                Material Loads
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => openLoadDialog()}
                className="h-8 text-xs border-[#ffd108] text-[#ffd108] hover:bg-[#ffd108]/10"
                data-testid="add-load-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Load
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {materialLoads.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Mountain className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No material loads added</p>
                <p className="text-xs mt-1">Tap "Add Load" to record pickups or disposals</p>
              </div>
            ) : (
              <div className="space-y-3">
                {materialLoads.map((load, index) => (
                  <div 
                    key={index}
                    className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-3"
                    data-testid={`material-load-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">{load.material_name}</span>
                          <Badge className={getLoadTypeColor(load.load_type)}>
                            {getLoadTypeLabel(load.load_type)}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-400">{load.yard_name}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[#ffd108] font-semibold">
                            {load.quantity} {load.unit}
                          </span>
                          {load.tip_docket_number && (
                            <span className="text-xs text-gray-500">
                              Docket: {load.tip_docket_number}
                            </span>
                          )}
                          {load.tip_docket_photo && (
                            <Camera className="h-3 w-3 text-green-400" />
                          )}
                        </div>
                        {load.notes && (
                          <p className="text-xs text-gray-500 mt-1">{load.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-gray-400 hover:text-white"
                          onClick={() => openLoadDialog(index)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                          onClick={() => handleDeleteLoad(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Work Completed - Description for each item */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">Work Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={workCompleted}
              onChange={(e) => setWorkCompleted(e.target.value)}
              placeholder="Describe all work completed during this job..."
              className="bg-[#1a1a1a] border-gray-600 text-white"
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="bg-[#1a1a1a] border-gray-600 text-white"
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Customer Sign-off */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <PenTool className="h-4 w-4 text-[#ffd108]" />
              Customer Sign-off
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-400 text-xs">Customer Name</Label>
              <div className="flex items-center gap-2 mt-1">
                <User className="h-4 w-4 text-gray-500" />
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Name of person signing"
                  className="bg-[#1a1a1a] border-gray-600 text-white"
                />
              </div>
            </div>

            {/* Signature Pad */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-400 text-xs">Signature</Label>
                {hasSignature && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearSignature}
                    className="text-red-400 h-6 px-2"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="border border-gray-600 rounded-lg overflow-hidden">
                <canvas
                  ref={canvasRef}
                  width={300}
                  height={150}
                  className="w-full touch-none"
                  style={{ background: '#1a1a1a' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <p className="text-gray-500 text-xs mt-1 text-center">Draw signature above</p>
            </div>

            {/* Confirmation */}
            <div className="flex items-start gap-3 p-3 bg-[#1a1a1a] rounded-lg">
              <Checkbox
                id="confirm"
                checked={customerConfirmed}
                onCheckedChange={setCustomerConfirmed}
                className="mt-0.5 data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108]"
              />
              <label htmlFor="confirm" className="text-gray-300 text-sm cursor-pointer">
                I confirm that the work described above has been completed satisfactorily.
              </label>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#2a2a2a] border-t border-gray-700 p-4">
        <Button 
          className="w-full btn-volt" 
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Check className="h-4 w-4 mr-2" />
          )}
          Submit Docket
        </Button>
      </div>

      {/* Add/Edit Material Load Dialog */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="bg-[#2a2a2a] border-gray-700 text-white w-[calc(100%-2rem)] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="h-5 w-5 text-[#ffd108]" />
              {editingLoadIndex !== null ? 'Edit Load' : 'Add Load'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* Location */}
            <div>
              <Label className="text-gray-300">Location *</Label>
              <Select 
                value={loadForm.yard_id} 
                onValueChange={(v) => {
                  setLoadForm(prev => ({ 
                    ...prev, 
                    yard_id: v,
                    material_type_id: '', // Reset material when yard changes
                    unit: 'tonne'
                  }));
                }}
              >
                <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                  <SelectValue placeholder="Select tip or quarry" />
                </SelectTrigger>
                <SelectContent>
                  {yards.map(yard => (
                    <SelectItem key={yard.id} value={yard.id}>
                      {yard.name} ({yard.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {yards.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">No locations configured</p>
              )}
            </div>

            {/* Material Type */}
            <div>
              <Label className="text-gray-300">Material Type *</Label>
              <Select 
                value={loadForm.material_type_id} 
                onValueChange={(v) => {
                  // Auto-select unit if only one is available
                  const availableUnits = getAvailableUnits();
                  setLoadForm(prev => ({ 
                    ...prev, 
                    material_type_id: v,
                    unit: availableUnits.length === 1 ? availableUnits[0] : prev.unit
                  }));
                }}
                disabled={!loadForm.yard_id}
              >
                <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                  <SelectValue placeholder={loadForm.yard_id ? "Select material" : "Select location first"} />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableMaterials().map(mat => (
                    <SelectItem key={mat.id} value={mat.id}>
                      {mat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Load Type */}
            <div>
              <Label className="text-gray-300">Load Type *</Label>
              <Select 
                value={loadForm.load_type} 
                onValueChange={(v) => setLoadForm(prev => ({ ...prev, load_type: v }))}
              >
                <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="disposal">Disposal</SelectItem>
                  <SelectItem value="buyback">Buy Back</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quantity and Unit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Quantity *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={loadForm.quantity}
                  onChange={(e) => setLoadForm(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1a1a1a] border-gray-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Unit *</Label>
                <Select 
                  value={loadForm.unit} 
                  onValueChange={(v) => setLoadForm(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableUnits().map(unit => (
                      <SelectItem key={unit} value={unit}>
                        {unit === 'm3' ? 'Cubic Metre (m³)' : unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tip Docket Number */}
            <div>
              <Label className="text-gray-300">Tip Docket Number</Label>
              <Input
                value={loadForm.tip_docket_number}
                onChange={(e) => setLoadForm(prev => ({ ...prev, tip_docket_number: e.target.value }))}
                placeholder="Docket # from tip/quarry"
                className="bg-[#1a1a1a] border-gray-600 text-white mt-1"
              />
            </div>

            {/* Photo Upload */}
            <div>
              <Label className="text-gray-300">Docket Photo</Label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              <div className="mt-1">
                {loadForm.tip_docket_photo ? (
                  <div className="relative">
                    <img 
                      src={loadForm.tip_docket_photo} 
                      alt="Docket" 
                      className="w-full h-32 object-cover rounded border border-gray-600"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-7"
                      onClick={() => setLoadForm(prev => ({ ...prev, tip_docket_photo: null }))}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed border-gray-600 text-gray-400 hover:text-white"
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Take Photo
                  </Button>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-gray-300">Notes</Label>
              <Textarea
                value={loadForm.notes}
                onChange={(e) => setLoadForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="bg-[#1a1a1a] border-gray-600 text-white mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setLoadDialogOpen(false)}
              className="border-gray-600 text-gray-300 w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              className="btn-volt w-full sm:w-auto"
              onClick={handleSaveLoad}
            >
              {editingLoadIndex !== null ? 'Save Changes' : 'Add Load'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileCreateDocket;
